import { useRef } from 'react';
import { Link } from 'react-router';
import { ABOUT } from '@/data/about';
import { Reveal, useMotion, useViewportProgress } from '@components/motion';
import FigmaFrame from '@components/landing/FigmaFrame';

const HERO_PHOTO = '/about/hero-photo.webp';
const HERO_BG = '/about/hero-bg.webp';
const HERO_GLOW = '/about/hero-glow.svg';

/** Black pill "Get in touch" CTA with a white circular arrow. */
function GetInTouch({ className }: { className?: string }) {
    return (
        <Link
            to={ABOUT.hero.ctaHref || '/help'}
            className={`flex items-center gap-[24px] rounded-[80px] bg-[#1b1b1b] py-[6px] pl-[6px] pr-[32px] transition-transform duration-300 hover:scale-[1.02] ${className ?? ''}`}
        >
            <span className="flex items-center justify-center rounded-full bg-white p-[16px]">
                <svg className="size-[20px]" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                        d="M5 12h13M12 5l7 7-7 7"
                        stroke="#1b1b1b"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
            <span className="font-secondary text-[20px] font-medium leading-[30px] text-white">
                {ABOUT.hero.ctaLabel}
            </span>
        </Link>
    );
}

function AboutHeroDesktop() {
    const frameRef = useRef<HTMLDivElement>(null);
    const glowRef = useRef<HTMLDivElement>(null);
    const photoRef = useRef<HTMLImageElement>(null);
    const progress = useViewportProgress(frameRef);

    // Layered scroll parallax — glow and photo drift at different speeds.
    useMotion(glowRef, progress, (el, p) => {
        el.style.transform = `translateY(${(p - 0.5) * -120}px)`;
    });
    useMotion(photoRef, progress, (el, p) => {
        el.style.transform = `translateY(${(p - 0.5) * 48}px) scale(1.08)`;
    });

    return (
        <FigmaFrame designWidth={1920} designHeight={1112} className="w-full">
            <div ref={frameRef} className="relative h-[1112px] w-[1920px] overflow-hidden bg-[#e5e5e5]">
                {/* Background artwork */}
                <img
                    src={ABOUT.hero.background || HERO_BG}
                    alt=""
                    className="pointer-events-none absolute inset-0 size-full object-cover"
                />

                {/* Glow ellipses */}
                <div
                    ref={glowRef}
                    className="pointer-events-none absolute left-[92px] top-[836px] flex h-[677px] w-[1802px] items-center justify-center will-change-transform"
                >
                    <img src={HERO_GLOW} alt="" className="w-full rotate-[5.96deg]" />
                </div>

                {/* Bottom photo band */}
                <Reveal
                    as="div"
                    once
                    variant="fade"
                    duration={1100}
                    className="absolute left-0 top-[734px] h-[378px] w-[1920px] overflow-hidden"
                >
                    <img
                        ref={photoRef}
                        src={HERO_PHOTO}
                        alt=""
                        className="absolute inset-0 size-full object-cover will-change-transform"
                    />
                    <div className="absolute inset-0 bg-black/20" />
                </Reveal>

                {/* Centre content */}
                <div className="absolute left-1/2 top-[396px] flex -translate-x-1/2 -translate-y-1/2 items-end gap-[341px]">
                    {/* Left column */}
                    <Reveal
                        as="div"
                        once
                        variant="fade-up"
                        delay={200}
                        className="flex w-[225px] flex-col items-start gap-[31px]"
                    >
                        <p className="text-[14px] font-medium leading-[20px] text-[#050505]">{ABOUT.hero.lead}</p>
                        <GetInTouch className="w-full" />
                    </Reveal>

                    {/* Headline group */}
                    <div className="relative h-[320px] w-[879.744px]">
                        <Reveal
                            as="p"
                            once
                            variant="fade"
                            delay={120}
                            className="absolute left-[584px] top-[111px] w-[162px] text-[18px] font-medium capitalize leading-[21px] text-[#050505]"
                        >
                            Justice, Made Reachable
                        </Reveal>
                        <Reveal
                            as="p"
                            once
                            variant="fade-up"
                            className="absolute left-[calc(50%-391.13px)] top-0 w-[489px] font-primary text-[170px] font-bold leading-[0.94] tracking-[-8.5px] text-[#050505]"
                        >
                            About
                            <br />
                            <span className="text-[rgba(5,5,5,0.3)]">us</span>
                        </Reveal>
                        <div className="absolute left-[261.74px] top-[280px] size-[29px] rounded-full bg-[#f60]" />
                        <Reveal
                            as="p"
                            once
                            variant="fade-up"
                            delay={260}
                            className="absolute left-[383.74px] top-[202px] w-[496px] text-[16px] font-normal leading-[24px] text-[#151515]"
                        >
                            {ABOUT.hero.body}
                        </Reveal>
                    </div>
                </div>
            </div>
        </FigmaFrame>
    );
}

function AboutHeroMobile() {
    return (
        <section className="relative overflow-hidden bg-[#e5e5e5] px-5 pb-0 pt-[120px]">
            {/* Background artwork */}
            <img
                src={ABOUT.hero.background || HERO_BG}
                alt=""
                className="pointer-events-none absolute inset-0 size-full object-cover"
            />

            <div className="relative">
                <p className="max-w-[280px] text-[13px] font-medium leading-[18px] text-[#050505]">{ABOUT.hero.lead}</p>

                <h1 className="mt-8 font-primary text-[88px] font-bold leading-[0.94] tracking-[-3px] text-[#050505]">
                    About
                    <br />
                    <span className="text-[rgba(5,5,5,0.3)]">us</span>
                    <span className="ml-2 inline-block size-[14px] translate-y-[2px] rounded-full bg-[#f60] align-baseline" />
                </h1>

                <p className="mt-6 text-[14px] font-normal leading-[24px] text-[#151515]">{ABOUT.hero.body}</p>

                <GetInTouch className="mt-8" />
            </div>

            <div className="relative mt-10 h-[240px] w-full overflow-hidden">
                <img
                    src={ABOUT.hero.photo || HERO_PHOTO}
                    alt={ABOUT.hero.photo ? ABOUT.hero.photoAlt : ''}
                    className="absolute inset-0 size-full object-cover"
                />
                <div className="absolute inset-0 bg-black/20" />
            </div>
        </section>
    );
}

export default function AboutHero() {
    return (
        <>
            <div className="hidden lg:block">
                <AboutHeroDesktop />
            </div>
            <div className="lg:hidden">
                <AboutHeroMobile />
            </div>
        </>
    );
}
